import { useState, useEffect } from 'react';
import { Lock, ArrowRight, CheckCircle, Warning, ShieldWarning } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [reset2fa, setReset2fa] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (!t) {
      setError('Geen geldige reset-link — token ontbreekt');
    }
    setToken(t || '');
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Wachtwoord moet minimaal 8 tekens zijn');
      return;
    }
    if (password !== confirm) {
      setError('Wachtwoorden komen niet overeen');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password, reset_2fa: reset2fa }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Reset mislukt');
      }
      setDone(true);
      setTimeout(() => { window.location.href = '/login'; }, 3500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-white border border-zinc-200 rounded-sm p-8" data-testid="reset-password-page">
        <h1 className="font-heading text-2xl font-black tracking-tight mb-1">Nieuw wachtwoord</h1>
        <p className="text-sm text-zinc-500 mb-6">
          Kies een sterk wachtwoord (minimaal 8 tekens).
        </p>

        {done ? (
          <div className="space-y-4 text-center" data-testid="reset-success">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle size={28} className="text-emerald-600" weight="fill" />
            </div>
            <div className="text-sm text-zinc-700">
              Wachtwoord aangepast.
              {reset2fa && (
                <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
                  2FA is gereset — bij de volgende login krijg je een nieuwe QR-code om je authenticator opnieuw te koppelen.
                </div>
              )}
              <div className="mt-2">Je wordt doorgestuurd naar de login...</div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="password" placeholder="Nieuw wachtwoord" value={password}
                onChange={e => setPassword(e.target.value)} required minLength={8} autoFocus
                className="w-full pl-9 pr-4 py-2.5 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-zinc-400"
                data-testid="new-password-input"
              />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="password" placeholder="Bevestig wachtwoord" value={confirm}
                onChange={e => setConfirm(e.target.value)} required minLength={8}
                className="w-full pl-9 pr-4 py-2.5 text-sm border border-zinc-200 rounded-sm focus:outline-none focus:border-zinc-400"
                data-testid="confirm-password-input"
              />
            </div>

            <label className="flex items-start gap-2 pt-2 cursor-pointer select-none" data-testid="reset-2fa-toggle">
              <input
                type="checkbox" checked={reset2fa}
                onChange={e => setReset2fa(e.target.checked)}
                className="mt-0.5 cursor-pointer"
                data-testid="reset-2fa-checkbox"
              />
              <span className="text-xs text-zinc-700 leading-relaxed">
                <span className="font-medium">Ook authenticator (2FA) resetten</span><br />
                <span className="text-zinc-500">Aanvinken als je geen toegang meer hebt tot je authenticator-app. Je krijgt bij de volgende login een nieuwe QR-code.</span>
              </span>
            </label>
            {reset2fa && (
              <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2">
                <ShieldWarning size={14} className="flex-shrink-0 mt-0.5" weight="fill" />
                <span>Je 2FA wordt volledig gewist. Je moet hierna opnieuw enrollen via een QR-code.</span>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-sm p-2">
                <Warning size={14} className="flex-shrink-0 mt-0.5" weight="fill" />
                <span>{error}</span>
              </div>
            )}
            <button
              type="submit" disabled={loading || !token || !password || !confirm}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0F172A] text-white text-sm font-medium rounded-sm hover:bg-[#1E293B] disabled:opacity-50"
              data-testid="reset-submit-btn"
            >
              {loading ? 'Bezig...' : 'Wachtwoord opslaan'}
              <ArrowRight size={14} />
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
